import React from 'react';
import styled from '@emotion/styled';
import { emphasize, Grid } from '@mui/material';
import FileTypeDisplay from '../FileTypeDisplay';
import EllipsisTooltip from './EllipsisTooltip';
import TableStatus from './TableStatus';
import IngestManagementOverflowMenu from '@/components/IngestManagement/IngestManagementOverflowMenu';
import { IngestManagementPreview } from '@/exports/columnCommonComponents';
import { formatBytes } from '@/_utils/utils';
import ReadyToRegisterButton from '@/components/_common/ReadyToRegisterButton';

interface IngestManagementGridItemRenderProps {
  data: any;
  onGridClick?: (e: React.MouseEvent) => void;
}

const IngestManagementGridItemRender = ({ data, onGridClick }: IngestManagementGridItemRenderProps) => {
  const dimensions = {
    height: 190,
    width: 325
  };

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Wrapper>
        <PreviewWrapper>
          <IngestManagementPreview data={data} expanded={false} previewDimensions={dimensions} isGrid />
          <ActionIcons previewIcon={+(data?.file_type !== 'video' && data?.file_type !== 'subtitle')}>
            <IngestManagementOverflowMenu data={data} vertical />
          </ActionIcons>
          <ActionIconsOverlay />
        </PreviewWrapper>
        <HidSpan>{data.hid}</HidSpan>
        <StyledFileTypeDisplay type={data.file_type} />
        <DataDiv>
          <EllipsisTooltip text={data.filename} maxWidth={320} />
          <SizeSpan>{formatBytes(data.file_size)}</SizeSpan>
          {data.status === 'ready_for_ingest' && !data.auto_ingest ? (
            <ReadyToRegisterButton fileType={data.file_type} color="primary" onClick={onGridClick} />
          ) : (
            <TableStatus
              textOnly
              status={data.status}
              statusReason={(data.status && (data.status === 'failed' || data.status === 'in_error') && data.status_reason) || ''}
            />
          )}
        </DataDiv>
      </Wrapper>
    </Grid>
  );
};

export default IngestManagementGridItemRender;

const ActionIcons = styled.div<{ previewIcon: number }>`
  position: absolute;
  right: ${({ theme }) => theme.spacing(1.5)};
  bottom: ${({ theme }) => theme.spacing(1)};
  z-index: 1;

  .MuiIconButton-root {
    margin: -5px;

    &:hover {
      background: ${({ theme }) => emphasize(theme.palette.secondary.main + '9a', 0.1)};
    }
  }
`;

const Wrapper = styled.div`
  margin: ${({ theme }) => theme.spacing(1)};
  height: 100%;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const PreviewWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${({ theme }) => `1px solid ${theme.palette.divider}`};
  position: relative;
`;

const HidSpan = styled.span`
  font-size: 1rem;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  position: absolute;
  line-height: 1;
  top: ${({ theme }) => theme.spacing(1)};
  left: ${({ theme }) => theme.spacing(1.2)};
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
`;

const StyledFileTypeDisplay = styled(FileTypeDisplay)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1)};
  right: ${({ theme }) => theme.spacing(1)};
  z-index: 2;
`;

const DataDiv = styled.div`
  margin-top: ${({ theme }) => theme.spacing(0.75)};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: ${({ theme }) => theme.spacing(0.5)};
`;

const SizeSpan = styled.span`
  font-size: 1rem;
  text-transform: uppercase;
  opacity: 0.8;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const ActionIconsOverlay = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  right: 0;
  top: 0;
  background: ${({ theme }) =>
    `radial-gradient(circle at calc(100% - 10px) calc(100% - 30px) , ${theme.palette.background.default} 0%, transparent 40%)`};
  pointer-events: none;
  opacity: ${({ theme }) => (theme.palette.mode === 'dark' ? 1 : 0.4)};
`;
